import React, { useState } from "react";
import * as PropTypes from "prop-types";
import { dataPropType } from "../propTypes";
import { makeStyles } from "@material-ui/styles";
import classNames from "classnames";
import Button from "@material-ui/core/Button";
import Drawer from "@material-ui/core/Drawer";
import Hidden from "@material-ui/core/Hidden";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import styleConstants, { toolbarHeight } from "../styleConstants";
import AnnexWidget from "./widgets/AnnexWidget";
import AppBar from "./widgets/AppBar";
import CountryExplorer from "./countryExplorer/CountryExplorer";
import MainTitle from "./widgets/MainTitle";
import Navigation from "./navigation/Navigation";
import ReadersGuide from "./readersGuide/ReadersGuide";
import Themes from "./themes/Themes";
import "./App.scss";
import { defaultAreaId } from "../constants";

const drawerWidth = "15rem";

const useStyles = makeStyles(theme => ({
  annexDrawerPaper: {
    width: "100%",
    top: toolbarHeight,
    backgroundColor: `${styleConstants.colors.gray1}`
  },
  container: {
    fontFamily: "'Source Sans Pro', sans-serif;"
  },
  content: {
    margin: `${toolbarHeight} 0 0 ${drawerWidth}`,
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0
    }
  },
  countryExplorerDrawerPaper: {
    width: "100%",
    top: toolbarHeight,
    backgroundColor: `${styleConstants.colors.gray1}`
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: styleConstants.colors.gray1,
    borderRight: "none",
    paddingLeft: "2rem",
    display: "flex",
    justifyContent: "center"
  },
  readersGuide: {
    zIndex: theme.zIndex.drawer + 1,
    position: "fixed",
    left: 0,
    bottom: 0,
    width: "100%",
    backgroundColor: styleConstants.colors.gray1,
    borderTop: `2px solid ${styleConstants.colors.themeBlue}`
  },
  readersGuideButton: {
    minHeight: "2rem",
    fontSize: "1.25rem",
    fontWeight: styleConstants.fontWeights.book,
    color: styleConstants.colors.themeBlue
  },
  readersGuideButtonIcon: {
    marginLeft: "0.5rem"
  },
  readersGuideCloseButtonContainer: {
    position: "fixed",
    width: "100%",
    borderBottom: `2px solid ${styleConstants.colors.themeBlue}`,
    backgroundColor: "white",
    zIndex: 1
  },
  readersGuideDrawerPaper: {
    height: `calc(100% - ${toolbarHeight})`,
    top: toolbarHeight
  },
  toolbarHeight: {
    height: toolbarHeight
  }
}));

const App = props => {
  const database = props.data && props.data.databases.core;

  const classes = useStyles();

  const [navigationOpen, setNavigationOpen] = useState(false);
  const [countryExplorerOpen, setCountryExplorerOpen] = useState(false);
  const [annexOpen, setAnnexOpen] = useState(false);
  const [annexAreaId, setAnnexAreaId] = useState(defaultAreaId);
  const [readersGuideOpen, setReadersGuideOpen] = useState(false);

  const handleAnnexAreaChange = areaId => {
    setAnnexAreaId(areaId);

    props.onAnnexAreaChange(areaId);
  };

  return (
    <div className={classNames("App", classes.container)}>
      <AppBar
        enableCountryExplorerOpening={!!props.data}
        enableAnnexOpening={!!props.data}
        onOpenNavigation={() => {
          setNavigationOpen(true);
        }}
        onOpenCountryExplorer={() => {
          setCountryExplorerOpen(true);
        }}
        onOpenAnnex={() => {
          setAnnexOpen(true);
        }}
      />

      <Hidden smDown>
        <Drawer
          variant="permanent"
          className={classes.drawer}
          classes={{
            paper: classes.drawerPaper
          }}
        >
          <div className={classes.toolbarHeight} />
          <Navigation />
        </Drawer>
      </Hidden>

      <Hidden mdUp>
        <Drawer
          variant="temporary"
          open={navigationOpen}
          className={classes.drawer}
          classes={{
            paper: classes.drawerPaper
          }}
          onClose={() => {
            setNavigationOpen(false);
          }}
        >
          <div className={classes.toolbarHeight} />
          <Navigation />
        </Drawer>
      </Hidden>

      <div className={classes.content}>
        <MainTitle />

        <Themes
          data={props.data}
          onOpenCountryExplorer={() => {
            setCountryExplorerOpen(true);
          }}
          onOpenDataAnnex={() => {
            setAnnexOpen(true);
          }}
          onWidgetChange={props.onWidgetChange}
          onDownloadCsv={props.onDownloadCsv}
        />

        <div className={classes.readersGuide}>
          <Button
            href={null}
            fullWidth={true}
            className={classes.readersGuideButton}
            onClick={() => {
              setReadersGuideOpen(true);
            }}
          >
            Reader's Guide
            <ExpandLessIcon className={classes.readersGuideButtonIcon} />
          </Button>
        </div>
      </div>

      <Drawer
        anchor="bottom"
        open={readersGuideOpen}
        classes={{
          paper: classes.readersGuideDrawerPaper
        }}
        onClose={() => {
          setReadersGuideOpen(false);
        }}
      >
        <div className={classes.readersGuideCloseButtonContainer}>
          <Button
            href={null}
            fullWidth={true}
            className={classes.readersGuideButton}
            onClick={() => {
              setReadersGuideOpen(false);
            }}
          >
            Reader's Guide
            <ExpandMoreIcon className={classes.readersGuideButtonIcon} />
          </Button>
        </div>

        <ReadersGuide />
      </Drawer>

      <Drawer
        anchor="right"
        open={countryExplorerOpen}
        transitionDuration={0}
        classes={{
          paper: classes.countryExplorerDrawerPaper
        }}
        onClose={() => {
          setCountryExplorerOpen(false);
        }}
      >
        <CountryExplorer
          data={props.data && props.data}
          onWidgetChange={props.onCountryExplorerWidgetChange}
          onClose={() => {
            setCountryExplorerOpen(false);
          }}
        />
      </Drawer>

      <Drawer
        anchor="right"
        open={annexOpen}
        transitionDuration={0}
        classes={{
          paper: classes.annexDrawerPaper
        }}
        onClose={() => {
          setAnnexOpen(false);
        }}
      >
        <AnnexWidget
          database={database}
          areaId={annexAreaId}
          data={props.data}
          onAreaChange={handleAnnexAreaChange}
          onClose={() => {
            setAnnexOpen(false);
          }}
          onDownloadCsv={props.onDownloadCsv}
        />
      </Drawer>
    </div>
  );
};

App.propTypes = {
  data: dataPropType,
  onWidgetChange: PropTypes.func.isRequired,
  onCountryExplorerWidgetChange: PropTypes.func.isRequired,
  onAnnexAreaChange: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired
};

export default App;
